import React, { Suspense } from "react";
import { Col, Form, FormControlProps, Spinner } from "react-bootstrap";
import {
  Control,
  FieldValues,
  RegisterOptions,
  useController,
  FieldPath,
  PathValue,
} from "react-hook-form";

const AsyncSelect = React.lazy(() => import("react-select/async"));

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends FormControlProps {
  component?: React.FC;
  xs?: string | number;
  lg?: string | number;
  md?: string | number;
  sm?: string | number;
  label?: string;
  loadOptions: (inputValue: string) => Promise<any[]>;
  className?: string;
  label_class?: string;
  group_class?: string;
  invalid?: string;
  loading?: boolean;
  placeholder?: string;
  children?: React.ReactElement;
  name: TName | any;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, TName>;
  defaultValue?: PathValue<TFieldValues, TName>;
  styles?: any;
  isMulti?: boolean;
}

function SelectAsync<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  component,
  lg,
  md,
  xs,
  sm,
  label,
  loading,
  placeholder,
  label_class,
  group_class,
  className,
  loadOptions,
  name,
  control,
  children,
  rules,
  defaultValue,
  ...rest
}: Props<TFieldValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue: defaultValue,
  });

  return (
    <Form.Group
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      as={Col}
      className={group_class}
    >
      <Suspense
        fallback={
          <div className="text-center">
            <Spinner className="" animation="border" role="status" />
          </div>
        }
      >
        {label ? (
          <Form.Label className={label_class}>{label}</Form.Label>
        ) : null}

        <AsyncSelect
          {...rest}
          {...field}
          cacheOptions
          isClearable
          defaultOptions
          loadOptions={loadOptions}
          placeholder={placeholder}
        />
      </Suspense>

      <Form.Control.Feedback type="invalid">
        {error?.message}
      </Form.Control.Feedback>

      {children ? children : null}
    </Form.Group>
  );
}

export default SelectAsync;
